import React, { useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import type { CatalogTemplateRow, WizardState } from './types';
import { buildTemplateRows } from './generateTemplateCsv';
import type { TemplateRow } from './generateTemplateCsv';

interface ReviewStepProps {
  state: WizardState;
  catalogRows: CatalogTemplateRow[];
  isLoading: boolean;
}

const MAX_PREVIEW_ROWS = 50;

const VITAL_ELEMENT_LABELS: Record<string, string> = {
  parentSku: 'Parent SKU',
  colorCode: 'Color Code',
  sizeCode: 'Size Code',
};

function getPricingLabel(state: WizardState): string {
  switch (state.pricingMode) {
    case 'blank': return 'Prices blank';
    case 'resalePrice': return 'Prices = base resale';
    case 'percentOff': return `Prices = ${state.percentOff}% off resale`;
  }
}

function formatPrice(cents: string): string {
  if (!cents) return '—';
  const n = parseInt(cents, 10);
  if (Number.isNaN(n)) return cents;
  return `${cents} ($${(n / 100).toFixed(2)})`;
}

const ReviewStep = ({ state, catalogRows, isLoading }: ReviewStepProps) => {
  const templateRows: TemplateRow[] = useMemo(
    () => (isLoading ? [] : buildTemplateRows(catalogRows, state)),
    [catalogRows, state, isLoading],
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">Loading catalog items...</Typography>
      </Box>
    );
  }

  const vitalElementsLabel = state.selectedVitalElements
    .map(el => VITAL_ELEMENT_LABELS[el])
    .join(' + ');

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${templateRows.length} rows`} size="small" />
        <Chip label={vitalElementsLabel} size="small" />
        <Chip label={getPricingLabel(state)} size="small" />
        {state.reasonCode && <Chip label={state.reasonCode} size="small" />}
      </Box>

      {templateRows.length === 0 ? (
        <Alert severity="warning">
          No products match your selections. Go back and widen the scope.
        </Alert>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 340 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>parent_sku</TableCell>
                  <TableCell>sku_subcode</TableCell>
                  <TableCell>color_code</TableCell>
                  <TableCell>size_code</TableCell>
                  <TableCell>override_reason_code</TableCell>
                  <TableCell>override_price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templateRows.slice(0, MAX_PREVIEW_ROWS).map((row, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {row.parent_sku || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {row.sku_subcode || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {row.color_code || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {row.size_code || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {row.override_code || '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {formatPrice(row.override_price)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {templateRows.length > MAX_PREVIEW_ROWS && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {'Showing '}
              {MAX_PREVIEW_ROWS}
              {' of '}
              {templateRows.length}
              {' rows'}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ReviewStep;
